import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { lazy, Suspense } from "react";
import { useAnalyticsTracker } from "@/hooks/useAnalyticsTracker";
import Index from "./pages/Index";
import ExperienceLab from "./pages/ExperienceLab";
import ServicePage from "./pages/services/ServicePage";
import CxHubPage from "./pages/cx/CxHubPage";
import CxServicePage from "./pages/cx/CxServicePage";
import CxAssessmentPage from "./pages/cx/CxAssessmentPage";
import NotFound from "./pages/NotFound";
import CookieConsent from "./components/CookieConsent";

// Legal pages
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import TermsOfService from "./pages/legal/TermsOfService";
import CookiePolicy from "./pages/legal/CookiePolicy";
import GDPRPolicy from "./pages/legal/GDPRPolicy";

// Admin pages (eager loaded for instant navigation)
import AdminLogin from "./pages/admin/AdminLogin";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard from "./pages/admin/Dashboard";
import Analytics from "./pages/admin/Analytics";
import BlogManager from "./pages/admin/BlogManager";
import BlogEditor from "./pages/admin/BlogEditor";
import RssScraper from "./pages/admin/RssScraper";
import Newsletter from "./pages/admin/Newsletter";
import Subscribers from "./pages/admin/Subscribers";
import InboxPage from "./pages/admin/InboxPage";
import SettingsPage from "./pages/admin/SettingsPage";
import CommentsManager from "./pages/admin/CommentsManager";
import ContactsPage from "./pages/admin/ContactsPage";
import ConversationsPage from "./pages/admin/ConversationsPage";
import ReportsPage from "./pages/admin/ReportsPage";
import GeoToolsPage from "./pages/admin/GeoToolsPage";
import ChecklistsPage from "./pages/admin/ChecklistsPage";
import ProjectsPage from "./pages/admin/ProjectsPage";
import CxRequestsPage from "./pages/admin/CxRequestsPage";

// Blog pages (lazy loaded)
const BlogLayout = lazy(() => import("./pages/blog/BlogLayout"));
const BlogHome = lazy(() => import("./pages/blog/BlogHome"));
const CategoryPage = lazy(() => import("./pages/blog/CategoryPage"));
const ArticlePage = lazy(() => import("./pages/blog/ArticlePage"));

const queryClient = new QueryClient();

const AdminFallback = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--off-white)' }}>
    <div className="animate-pulse text-muted-foreground">Loading…</div>
  </div>
);

/** Wrapper that provides pathname to the analytics tracker */
const AnalyticsWrapper = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  useAnalyticsTracker(location.pathname);
  return <>{children}</>;
};

// Application root
const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <CookieConsent />
          <AnalyticsWrapper>
            <Suspense fallback={<AdminFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/experience-lab" element={<ExperienceLab />} />
                <Route path="/services/:slug" element={<ServicePage />} />

                {/* CX Services */}
                <Route path="/cx" element={<CxHubPage />} />
                <Route path="/cx/services/:slug" element={<CxServicePage />} />
                <Route path="/cx/assessment" element={<CxAssessmentPage />} />

                {/* Legal pages */}
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/cookies" element={<CookiePolicy />} />
                <Route path="/gdpr" element={<GDPRPolicy />} />

                {/* Blog routes */}
                <Route path="/blog" element={<BlogLayout />}>
                  <Route index element={<BlogHome />} />
                  <Route path="news" element={<CategoryPage />} />
                  <Route path="tech" element={<CategoryPage />} />
                  <Route path="ai" element={<CategoryPage />} />
                  <Route path="innovation" element={<CategoryPage />} />
                  <Route path="opinion" element={<CategoryPage />} />
                  <Route path=":slug" element={<ArticlePage />} />
                </Route>

                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="analytics" element={<Analytics />} />
                  <Route path="blog" element={<BlogManager />} />
                  <Route path="blog/new" element={<BlogEditor />} />
                  <Route path="blog/edit/:id" element={<BlogEditor />} />
                  <Route path="comments" element={<CommentsManager />} />
                  <Route path="rss" element={<RssScraper />} />
                  <Route path="newsletter" element={<Newsletter />} />
                  <Route path="subscribers" element={<Subscribers />} />
                  <Route path="contacts" element={<ContactsPage />} />
                  <Route path="conversations" element={<ConversationsPage />} />
                  <Route path="reports" element={<ReportsPage />} />
                  <Route path="geo" element={<GeoToolsPage />} />
                  <Route path="checklists" element={<ChecklistsPage />} />
                  <Route path="projects" element={<ProjectsPage />} />
                  <Route path="cx-requests" element={<CxRequestsPage />} />
                  <Route path="inbox" element={<InboxPage />} />
                  <Route path="settings" element={<SettingsPage />} />
                </Route>

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </AnalyticsWrapper>
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
